import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  Certificate, Clock, Warning, CheckCircle, Shield,
  SpinnerGap, ArrowRight, CalendarCheck, Users
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { canAccessAdmin } from "../utils/permissions";

import { API_BASE as API } from "../lib/apiBase";

export default function CertificationsPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [myCerts, setMyCerts] = useState([]);
  const [orgCerts, setOrgCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("my");

  // iter115 (Eric 2026-06-24) — Migration vers helper canonique canAccessAdmin.
  const isAdmin = canAccessAdmin(user);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboarding/certificates`, { withCredentials: true });
        setMyCerts(data);
        if (isAdmin && user?.org_id) {
          const { data: org } = await axios.get(`${API}/api/onboarding/certificates/org`, { withCredentials: true });
          setOrgCerts(org);
        }
      } catch {}
      setLoading(false);
    })();
  }, [isAdmin, user?.org_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  const certs = tab === "my" ? myCerts : orgCerts;

  const getStatusBadge = (cert) => {
    if (cert.status === "expired") {
      return (
        <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400" data-testid={`cert-status-${cert.id}`}>
          <Warning size={12} /> Expire
        </span>
      );
    }
    if (cert.expiring_soon) {
      return (
        <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400" data-testid={`cert-status-${cert.id}`}>
          <Clock size={12} /> Expire dans {cert.days_until_expiry}j
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400" data-testid={`cert-status-${cert.id}`}>
        <CheckCircle size={12} weight="fill" /> Valide
        {cert.days_until_expiry != null && <span className="text-[10px] opacity-70 ml-1">({cert.days_until_expiry}j)</span>}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">{t("certifications_title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">Conformité et certifications de formation</p>
        </div>
        <Shield size={28} className="text-blue-500" />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4">
          <div className="text-sm text-zinc-500 mb-1">Actives</div>
          <div className="text-2xl font-bold text-teal-500">{myCerts.filter(c => c.status !== "expired").length}</div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4">
          <div className="text-sm text-zinc-500 mb-1">Expirees</div>
          <div className="text-2xl font-bold text-red-500">{myCerts.filter(c => c.status === "expired").length}</div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4">
          <div className="text-sm text-zinc-500 mb-1">Expire bientot</div>
          <div className="text-2xl font-bold text-amber-500">{myCerts.filter(c => c.expiring_soon).length}</div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4">
          <div className="text-sm text-zinc-500 mb-1">Score moyen</div>
          <div className="text-2xl font-bold">{myCerts.length > 0 ? Math.round(myCerts.reduce((s, c) => s + (c.avg_score || 0), 0) / myCerts.length) : 0}%</div>
        </div>
      </div>

      {/* Tabs */}
      {isAdmin && (
        <div className="flex gap-2">
          <button onClick={() => setTab("my")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "my" ? "bg-blue-600 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"}`}
            data-testid="tab-my-certs">
            <Certificate size={14} className="inline mr-1" /> Mes certifications
          </button>
          <button onClick={() => setTab("org")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "org" ? "bg-blue-600 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"}`}
            data-testid="tab-org-certs">
            <Users size={14} className="inline mr-1" /> Équipe ({orgCerts.length})
          </button>
        </div>
      )}

      {/* Certificates List */}
      <div className="space-y-3" data-testid="certs-list">
        {certs.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-12 text-center">
            <Certificate size={48} className="mx-auto mb-4 text-zinc-300 dark:text-zinc-600" />
            <h3 className="font-semibold mb-1">Aucune certification</h3>
            <p className="text-sm text-zinc-500 mb-4">Completez un parcours de formation pour obtenir votre premiere certification</p>
            <a href="/dashboard/onboarding" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500">
              <ArrowRight size={16} /> Voir les parcours
            </a>
          </div>
        ) : (
          certs.map((cert) => (
            <div key={cert.id} className={`bg-white dark:bg-zinc-900 border rounded-xl p-5 flex items-center gap-4 transition-all ${
              cert.status === "expired" ? "border-red-200 dark:border-red-900/50" :
              cert.expiring_soon ? "border-amber-200 dark:border-amber-900/50" :
              "border-zinc-200 dark:border-zinc-800"
            }`} data-testid={`cert-card-${cert.id}`}>
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                cert.status === "expired" ? "bg-red-50 dark:bg-red-900/20" :
                cert.expiring_soon ? "bg-amber-50 dark:bg-amber-900/20" :
                "bg-teal-50 dark:bg-teal-900/20"
              }`}>
                <Certificate size={28} className={
                  cert.status === "expired" ? "text-red-500" :
                  cert.expiring_soon ? "text-amber-500" :
                  "text-teal-500"
                } weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-0.5">
                  <h3 className="font-semibold text-sm truncate">{cert.pathway_title}</h3>
                  {getStatusBadge(cert)}
                </div>
                {tab === "org" && <p className="text-xs text-zinc-500 mb-0.5">{cert.user_name}</p>}
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <span className="flex items-center gap-1">
                    <CalendarCheck size={12} />
                    Obtenu le {new Date(cert.completed_at).toLocaleDateString("fr-FR")}
                  </span>
                  {cert.expires_at && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      Expire le {new Date(cert.expires_at).toLocaleDateString("fr-FR")}
                    </span>
                  )}
                  <span>Score: {cert.avg_score}%</span>
                </div>
              </div>
              {cert.status === "expired" && (
                <a href="/dashboard/onboarding" className="flex-shrink-0 px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-400" data-testid={`renew-cert-${cert.id}`}>
                  Renouveler
                </a>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
